// src/pages/AuditLog.js
// Activity audit log — view all automation and user actions

import React, { useState, useEffect, useCallback } from 'react';
import * as awsApi from '../services/awsApiService';
import * as apiService from '../services/apiService';
import {
  ShieldCheck, RefreshCw, Search, X, ChevronLeft, ChevronRight,
  Zap, FileText, DollarSign, Mail, AlertTriangle, CheckCircle, Clock
} from 'lucide-react';

const PAGE_SIZE = 50;

const ACTION_META = {
  auto_appeal_generated:  { label: 'Auto Appeal Generated', icon: FileText,    color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)' },
  auto_fax_sent:          { label: 'Appeal Faxed',          icon: FileText,    color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)' },
  claim_written_off:      { label: 'Claim Written Off',     icon: AlertTriangle,color: '#ef4444', bg: 'rgba(239,68,68,0.1)' },
  claim_aging_flagged:    { label: 'Aging Flagged',         icon: Clock,       color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
  claim_scrubbed:         { label: 'Claim Scrubbed',        icon: CheckCircle, color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
  payment_link_sent:      { label: 'Payment Link Sent',     icon: DollarSign,  color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
  patient_payment_received:{ label: 'Payment Received',     icon: DollarSign,  color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
  era_payment_posted:     { label: 'ERA Posted',            icon: DollarSign,  color: '#3b82f6', bg: 'rgba(59,130,246,0.1)' },
  statement_sent:         { label: 'Statement Sent',        icon: Mail,        color: '#06b6d4', bg: 'rgba(6,182,212,0.1)' },
  document_deleted:       { label: 'Document Deleted',      icon: FileText,    color: '#64748b', bg: 'rgba(100,116,139,0.1)' },
  preauth_created:        { label: 'Pre-Auth Created',      icon: ShieldCheck, color: '#6366f1', bg: 'rgba(99,102,241,0.1)' },
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

function ActionBadge({ action }) {
  const m = ACTION_META[action] || { label: action.replace(/_/g, ' '), icon: Zap, color: '#64748b', bg: 'rgba(100,116,139,0.1)' };
  const Icon = m.icon;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, background: m.bg, color: m.color, fontSize: 12, fontWeight: 600 }}>
      <Icon style={{ width: 11, height: 11 }} />
      {m.label}
    </span>
  );
}

function MetaSummary({ action, metadata }) {
  if (!metadata) return null;
  try {
    const m = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
    const parts = [];
    if (m.patient || m.patient_name) parts.push(m.patient || m.patient_name);
    if (m.payer) parts.push(m.payer);
    if (m.amount != null) parts.push(`$${parseFloat(m.amount).toFixed(2)}`);
    if (m.claim_number) parts.push(m.claim_number);
    if (m.reason) parts.push(m.reason);
    if (m.days_old) parts.push(`${m.days_old} days old`);
    if (m.file_name) parts.push(m.file_name);
    return <span style={{ fontSize: 12, color: '#64748b' }}>{parts.join(' · ')}</span>;
  } catch {
    return null;
  }
}

const DEMO_LOGS = [
  { id: '1', action: 'auto_appeal_generated', metadata: { patient: 'Robert Johnson', payer: 'Cigna', amount: 285 }, created_at: '2026-05-23T14:30:00Z', user_email: 'automation' },
  { id: '2', action: 'auto_fax_sent',          metadata: { patient: 'Robert Johnson', payer: 'Cigna' },              created_at: '2026-05-23T14:31:00Z', user_email: 'automation' },
  { id: '3', action: 'claim_scrubbed',         metadata: { patient: 'Sarah Mitchell', payer: 'Delta Dental', amount: 420 }, created_at: '2026-05-23T02:15:00Z', user_email: 'automation' },
  { id: '4', action: 'era_payment_posted',     metadata: { payer: 'Delta Dental', amount: 312.50 },                 created_at: '2026-05-22T09:00:00Z', user_email: 'automation' },
  { id: '5', action: 'payment_link_sent',      metadata: { patient: 'Maria Garcia', amount: 45.00, email: 'maria@email.com' }, created_at: '2026-05-22T09:01:00Z', user_email: 'automation' },
  { id: '6', action: 'statement_sent',         metadata: { patient: 'Emily Chen', amount: 120.00 },                 created_at: '2026-05-21T16:00:00Z', user_email: 'admin@demo.com' },
  { id: '7', action: 'claim_aging_flagged',    metadata: { patient: 'James Wilson', payer: 'MetLife', days_old: 45 }, created_at: '2026-05-21T02:00:00Z', user_email: 'automation' },
  { id: '8', action: 'preauth_created',        metadata: { patient: 'Lisa Thompson', payer: 'Aetna' },              created_at: '2026-05-20T11:30:00Z', user_email: 'admin@demo.com' },
];

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const res = await awsApi.getAuditLogs(null, { limit: PAGE_SIZE, offset: page * PAGE_SIZE });
    if (res.success !== false) {
      setLogs(res.logs || res.data || []);
      setTotal(res.total || 0);
    }
    setLoading(false);
  }, [page]);

  useEffect(() => {
    if (apiService.getAppMode() === 'demo') {
      setLogs(DEMO_LOGS);
      setTotal(DEMO_LOGS.length);
      setLoading(false);
      return;
    }
    load();
  }, [load, page]);

  const filtered = logs.filter(log => {
    if (actionFilter && log.action !== actionFilter) return false;
    if (search) {
      const meta = typeof log.metadata === 'string' ? log.metadata : JSON.stringify(log.metadata || {});
      const haystack = `${log.action} ${meta} ${log.user_email || ''}`.toLowerCase();
      if (!haystack.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const uniqueActions = [...new Set(logs.map(l => l.action))].sort();

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 }}>Activity Log</h1>
        <p style={{ color: '#64748b', fontSize: 14, marginTop: 4 }}>
          Complete audit trail of automation actions and user activity. {total > 0 && `${total} total entries.`}
        </p>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 320 }}>
          <Search style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: '#94a3b8' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search logs..."
            style={{ width: '100%', padding: '8px 12px 8px 30px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              <X style={{ width: 13, height: 13, color: '#94a3b8' }} />
            </button>
          )}
        </div>
        <select
          value={actionFilter}
          onChange={e => setActionFilter(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', color: '#374151' }}
        >
          <option value="">All actions</option>
          {uniqueActions.map(a => (
            <option key={a} value={a}>{ACTION_META[a]?.label || a.replace(/_/g, ' ')}</option>
          ))}
        </select>
        {apiService.getAppMode() !== 'demo' && (
          <button
            onClick={load}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', fontSize: 13, cursor: 'pointer', color: '#374151' }}
          >
            <RefreshCw style={{ width: 14, height: 14 }} /> Refresh
          </button>
        )}
      </div>

      {/* Log list */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading activity...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <ShieldCheck style={{ width: 40, height: 40, color: '#cbd5e1', margin: '0 auto 12px' }} />
            <p style={{ color: '#64748b', fontWeight: 600 }}>No activity found</p>
            <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>Automation actions and user events will appear here as they happen.</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                {['Time', 'Action', 'Details', 'By'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(log => (
                <tr key={log.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '11px 16px', fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{formatDate(log.created_at)}</td>
                  <td style={{ padding: '11px 16px' }}><ActionBadge action={log.action} /></td>
                  <td style={{ padding: '11px 16px' }}><MetaSummary action={log.action} metadata={log.metadata} /></td>
                  <td style={{ padding: '11px 16px', fontSize: 12, color: '#64748b' }}>
                    {log.user_email === 'automation' || !log.user_email ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#8b5cf6' }}>
                        <Zap style={{ width: 11, height: 11 }} /> automation
                      </span>
                    ) : log.user_email}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: page === 0 ? 'default' : 'pointer', opacity: page === 0 ? 0.4 : 1, display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}
          >
            <ChevronLeft style={{ width: 14, height: 14 }} /> Prev
          </button>
          <span style={{ fontSize: 13, color: '#64748b' }}>Page {page + 1} of {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: page >= totalPages - 1 ? 'default' : 'pointer', opacity: page >= totalPages - 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}
          >
            Next <ChevronRight style={{ width: 14, height: 14 }} />
          </button>
        </div>
      )}
    </div>
  );
}
